//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { MediaFilterParamKey } from '@apis/content-library/contentLibraryApiModel';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { isDefined } from '@utils/filterPredicates';

export interface CheckboxSetFilterProps<T extends FilterValue> {
  isDisabled?: boolean;
  section: FilterSection<T>;
  subsection?: FilterSection<FilterValue>;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  isSingleSelect?: boolean;
}

export const CheckboxSetFilter = <T extends FilterValue>({
  isDisabled,
  isSingleSelect = false,
  onChange,
  section,
  subsection,
}: CheckboxSetFilterProps<T>) => {
  const handleSelect = (selected: FilterOption<T>) => {
    const options = section.options.map((option) => ({
      ...option,
      isSelected:
        option === selected ? !selected.isSelected : option.isSelected,
    }));

    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    updatedSections.set(section.paramKey, { ...section, options });

    onChange(updatedSections);
  };

  const handleSingleSelect = (selected: FilterOption<T>) => {
    // treated like a radio button - only one checkbox can be selected at a time
    const options = section.options.map((option) => ({
      ...option,
      isSelected: option === selected ? !selected.isSelected : false,
    }));
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    updatedSections.set(section.paramKey, { ...section, options });

    if (isDefined(subsection)) {
      // clear the subsection filters when the parent section changes
      updatedSections.set(subsection.paramKey, {
        ...subsection,
        options: [],
      });
    }

    onChange(updatedSections);
  };

  return (
    <FiledSet>
      <Legend>{FilterSectionTitle[section.paramKey]}</Legend>
      <CheckboxSet>
        {section.options.map((option) => (
          <Checkbox
            key={option.label}
            handleClick={() =>
              isSingleSelect ? handleSingleSelect(option) : handleSelect(option)
            }
            isChecked={option.isSelected}
            isDisabled={isDisabled}
            label={option.label}
            trackingId={option.trackingId}
          />
        ))}
      </CheckboxSet>
    </FiledSet>
  );
};

const FiledSet = styled.fieldset`
  padding: unset;
  border: unset;
`;

const Legend = styled.legend(
  ({ theme }) => `
    visibility: hidden;
    height: ${theme.spacings[0]};
    margin: ${theme.spacings[0]};
  `,
);

const CheckboxSet = styled.div`
  display: flex;
  flex-direction: column;
`;

const Checkbox = styled(LabeledCheckbox)(
  ({ theme }) => `
    height: unset;
    padding: ${theme.spacings[2]} ${theme.spacings[0]};
  `,
);
